import { useMemo } from "react";

interface ActivitySparklineProps {
  timestamps: number[];
  color?: string;
  width?: number;
  height?: number;
}

export function ActivitySparkline({
  timestamps,
  color = "#8000ff",
  width = 80,
  height = 16,
}: ActivitySparklineProps) {
  const points = useMemo(() => {
    if (timestamps.length < 2) return "";

    const now = Date.now();
    const windowMs = 60_000; // 1 minute window
    const bucketCount = 20;
    const bucketSize = windowMs / bucketCount;

    // Count events per bucket
    const buckets = new Array(bucketCount).fill(0);
    for (const ts of timestamps) {
      const age = now - ts;
      if (age > windowMs) continue;
      const bucket = Math.min(bucketCount - 1, Math.floor((windowMs - age) / bucketSize));
      buckets[bucket]++;
    }

    const max = Math.max(...buckets, 1);
    const padding = 1;

    return buckets
      .map((count, i) => {
        const x = padding + (i / (bucketCount - 1)) * (width - padding * 2);
        const y = height - padding - (count / max) * (height - padding * 2);
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  }, [timestamps, width, height]);

  if (timestamps.length < 2) return null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
    >
      <path
        d={points}
        fill="none"
        stroke={color}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.6}
      />
    </svg>
  );
}
